
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Post } from "@/types/board";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink,
  PaginationNext,
  PaginationPrevious 
} from "@/components/ui/pagination";

interface UserPostsSectionProps {
  userId: string;
}

const UserPostsSection = ({ userId }: UserPostsSectionProps) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const postsPerPage = 10;

  useEffect(() => {
    const fetchUserPosts = async () => {
      setLoading(true);
      try {
        // Get count for pagination
        const { count } = await supabase
          .from('board_posts')
          .select('*', { count: 'exact', head: true })
          .eq('author_id', userId);
          
        if (count !== null) {
          setTotalPages(Math.ceil(count / postsPerPage));
        }
        
        // Fetch posts with pagination
        const { data, error } = await supabase
          .from('board_posts')
          .select('*')
          .eq('author_id', userId)
          .order('created_at', { ascending: false })
          .range((page - 1) * postsPerPage, page * postsPerPage - 1);
          
        if (error) throw error;
        setPosts(data as Post[]);
      } catch (error) {
        console.error('Error fetching user posts:', error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserPosts();
    }
  }, [userId, page]);

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText size={18} />
          내가 쓴 글
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : posts.length > 0 ? (
          <>
            <ul className="divide-y">
              {posts.map((post) => (
                <li key={post.id} className="py-3">
                  <Link 
                    to={`/board/post/${post.id}`}
                    className="block hover:bg-muted p-2 rounded-md transition-colors"
                  >
                    <div className="flex justify-between">
                      <span className="font-medium text-sm text-primary">{post.category}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: ko })}
                      </span>
                    </div>
                    <p className="font-medium mt-1">{post.title}</p>
                    <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                      <span>조회 {post.view_count}</span>
                      <span>좋아요 {post.like_count}</span>
                      <span>댓글 {post.comment_count}</span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            
            {totalPages > 1 && (
              <Pagination className="mt-6">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => handlePageChange(page - 1)}
                      className={page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        onClick={() => handlePageChange(pageNum)}
                        isActive={page === pageNum}
                        className="cursor-pointer"
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  
                  <PaginationItem>
                    <PaginationNext
                      onClick={() => handlePageChange(page + 1)}
                      className={page === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            작성한 게시글이 없습니다.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UserPostsSection;
